function normalizeComplementaryOptionName(name) {
  return String(name ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function complementaryOptionStringsEqual(a, b) {
  const optionA = normalizeComplementaryOptionName(a);
  const optionB = normalizeComplementaryOptionName(b);

  if (!optionA || !optionB) return false;
  return optionA === optionB;
}

function getComplementaryOptionValuesList(optionMeta) {
  const { values } = optionMeta ?? {};
  return  (!Array.isArray(values)) ? [] : values;
}

function parseVariantPickerDatasetJson(picker, key) {
  try {
    return JSON.parse(picker.dataset[key]);
  } catch {
    return null;
  }
}

function buildMainVariantValuesByOptionName(mainVariant, mainOptionsWithValues) {
  const byName = new Map();
  if (!mainVariant || !Array.isArray(mainOptionsWithValues)) return byName;

  mainOptionsWithValues.forEach((opt, index) => {
    const nameKey = normalizeComplementaryOptionName(opt?.name);
    const value = mainVariant[`option${index + 1}`];
    if (nameKey && value != null && value !== '') byName.set(nameKey, value);
  });
  return byName;
}

function pickMainValueForRecommendedOption(recommendedOption, mainValuesByName, mainVariant, recommendedIndex) {
  const recommendedNameKey = normalizeComplementaryOptionName(recommendedOption?.name);
  if (recommendedNameKey) {
    for (const [mainNameKey, value] of mainValuesByName) {
      if (complementaryOptionStringsEqual(recommendedNameKey, mainNameKey)) return value;
    }
  }
  return mainVariant[`option${recommendedIndex + 1}`] ?? null;
}

function isValueAllowedOnRecommendedOption(wantedValue, recommendedOptionMeta) {
  return getComplementaryOptionValuesList(recommendedOptionMeta).some((allowed) =>
    complementaryOptionStringsEqual(allowed, wantedValue)
  );
}

function optionTripleFromVariant(variant) {
  return [variant?.option1, variant?.option2, variant?.option3];
}

function findVariantWithOptionTriple(variants, triple) {
  const [option1, option2, option3] = triple;
  return variants.find((variant) => variant.option1 === option1 && variant.option2 === option2 && variant.option3 === option3) ?? null;
}

function findComplementaryVariantMatchingMain(mainVariant, mainPicker, recommendedPicker) {
  if (!mainVariant || !mainPicker || !recommendedPicker) return null;

  const mainOptions = parseVariantPickerDatasetJson(mainPicker, 'options');
  const recommendedOptions = parseVariantPickerDatasetJson(recommendedPicker, 'options');
  if (!Array.isArray(recommendedOptions) || recommendedOptions.length === 0) return null;

  const mainValuesByName = buildMainVariantValuesByOptionName(mainVariant, mainOptions);
  const currentRecommended = recommendedPicker.variants.find((v) => v.id === recommendedPicker.selectedVariantId);
  const targetTriple = optionTripleFromVariant(currentRecommended);

  recommendedOptions.forEach((recommendedOptionMeta, index) => {
    const wanted = pickMainValueForRecommendedOption(
      recommendedOptionMeta,
      mainValuesByName,
      mainVariant,
      index
    );
    if (wanted == null || wanted === '') return;
    if (!isValueAllowedOnRecommendedOption(wanted, recommendedOptionMeta)) return;

    targetTriple[index] = wanted;
  });

  return findVariantWithOptionTriple(recommendedPicker.variants, targetTriple);
}

function getComplementaryPopupPickers(popup) {
  const [mainPicker, recommendedPicker] = popup.querySelectorAll('variant-picker-custom');
  return { mainPicker, recommendedPicker };
}

function stripImagePayloadFromVariantOrFallback(variant, imageWrap) {
  if (variant?.featured_image) return variant;
  const src = imageWrap?.dataset?.productFeaturedImageSrc;
  if (!src) return null;
  return {
    featured_image: {
      src,
      alt: imageWrap.dataset.productFeaturedImageAlt ?? ''
    }
  };
}

function applyPdpVariantToComplementaryProductsCustom(variant) {
  if (!variant?.id) return;

  const popup = document.querySelector('.complementary-products-popup');
  if (popup) {
    const { mainPicker, recommendedPicker } = getComplementaryPopupPickers(popup);
    mainPicker?.setVariant?.(variant.id);

    if (mainPicker && recommendedPicker) {
      const matched = findComplementaryVariantMatchingMain(variant, mainPicker, recommendedPicker);
      if (matched?.id) recommendedPicker.setVariant(matched.id);
    }
  }

  const mainWrap = document.querySelector('.complementary-products-custom [data-main-product-image]');
  const mainImg = mainWrap?.querySelector('img');
  if (mainImg) {
    const payload = stripImagePayloadFromVariantOrFallback(variant, mainWrap);
    if (payload?.featured_image?.src && typeof window.updateResponsiveImage === 'function') {
      window.updateResponsiveImage(mainImg, payload);
    }
  }

  const recommendedWrap = document.querySelector(
    '.complementary-products-custom__images .complementary-products-custom__image:not([data-main-product-image])'
  );
  const recommendedImg = recommendedWrap?.querySelector('img');
  if (!recommendedImg || !popup) return;

  const { recommendedPicker } = getComplementaryPopupPickers(popup);
  if (!recommendedPicker) return;

  const id = Number.parseInt(recommendedPicker.dataset.selectedVariantId, 10);
  const recommendedVariant = recommendedPicker.variants.find((v) => v.id === id);
  if (recommendedVariant?.featured_image?.src && typeof window.updateResponsiveImage === 'function') {
    window.updateResponsiveImage(recommendedImg, recommendedVariant);
  }
}

function syncComplementaryProductsCustomFromMainPdp() {
  const variantScript = document.querySelector(
    '.shopify-section--main-product variant-picker[update-url] script[data-variant]'
  );
  if (!variantScript?.textContent) return;

  let variant;
  try {
    variant = JSON.parse(variantScript.textContent);
  } catch {
    return;
  }

  requestAnimationFrame(() => applyPdpVariantToComplementaryProductsCustom(variant));
}

document.addEventListener('variant:change', function (e) {
  const variant = e.detail?.variant;
  if (!variant?.id) return;
  applyPdpVariantToComplementaryProductsCustom(variant);
});

document.addEventListener('complementary-products-custom:loaded', syncComplementaryProductsCustomFromMainPdp);
